import { css } from "styled-components";
import { getFontSetNames, getFontSetWeight } from "../../utils/getFontSetLabels";
import { AuthoryColorSchemes } from "../ProfileHero/V3ProfileHero.types";
import { theme } from "../../theme";
import { hexToRgba } from "../../utils/hexToRGBA";

export enum CustomDarkShade {
    Grey48 = "Grey48",
    Grey35 = "Grey35",
    Grey70 = "Grey70",
    BlackPearl1 = "BlackPearl1",
    BlackPearl = "BlackPearl"
}

export type AuthoryColorSchemeProps = {
    $colorVariant?: AuthoryColorSchemes;
    $customBackgroundColor?: string;
    $customDarkShade?: CustomDarkShade;
};

export const UserThemableHeading = css<AuthoryColorSchemeProps>`
    /* border: 1px solid red !important; */
    font-family: ${({ theme }) => getFontSetNames(theme.customization.profileFontSet).headingLabel}, sans-serif;
    font-weight: ${({ theme }) => getFontSetWeight(theme.customization.profileFontSet).headingLabel};
    color: ${({ theme, $colorVariant, $customBackgroundColor, $customDarkShade }) =>
        getFontColor($customBackgroundColor || theme.customization.backgroundColor, $colorVariant, $customDarkShade)};

    &::placeholder {
        color: ${({ theme, $colorVariant, $customBackgroundColor, $customDarkShade }) =>
            hexToRgba(getFontColor($customBackgroundColor || theme.customization.backgroundColor, $colorVariant, $customDarkShade), 0.6)};
    }
`;

export const UserThemableParagraph = css<AuthoryColorSchemeProps>`
    /* border: 1px solid green !important; */
    font-family: ${({ theme }) => getFontSetNames(theme.customization.profileFontSet).paragraphLabel}, sans-serif;
    font-weight: ${({ theme }) => getFontSetWeight(theme.customization.profileFontSet).paragraphLabel};
    color: ${({ theme, $colorVariant, $customBackgroundColor, $customDarkShade }) =>
        getFontColor($customBackgroundColor || theme.customization.backgroundColor, $colorVariant, $customDarkShade)};
`;

export const getFontColor = (backgroundColor: string, colorVariant?: AuthoryColorSchemes, customDarkShade?: CustomDarkShade) => {
    if (colorVariant === AuthoryColorSchemes.light) return "#FFFFFF";
    return getContrastingBW(backgroundColor, customDarkShade);
};

function getContrastRatio(l1: number, l2: number): number {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}

function getRelativeLuminance(r: number, g: number, b: number): number {
    // Convert sRGB to [0,1]
    const srgb = [r, g, b].map((v) => v / 255);

    const [R, G, B] = srgb.map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));

    // Relative luminance formula (WCAG)
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
    // Remove leading '#'
    hex = hex.replace(/^#/, "");

    // Handle short form (#fff)
    if (hex.length === 3) {
        hex = hex
            .split("")
            .map((ch) => ch + ch)
            .join("");
    }

    if (hex.length !== 6) {
        return { r: 0, g: 0, b: 0 };
        // throw new Error(`Invalid hex color: ${hex}`);
    }

    const num = parseInt(hex, 16);
    return {
        r: (num >> 16) & 0xff,
        g: (num >> 8) & 0xff,
        b: num & 0xff
    };
}

const getDarkShadeValue = (customDarkShade: CustomDarkShade) => {
    switch (customDarkShade) {
        case CustomDarkShade.Grey48:
            return theme.v3.colors.grey48;
        case CustomDarkShade.Grey35:
            return theme.v3.colors.grey35;
        case CustomDarkShade.Grey70:
            return theme.v3.colors.grey70;
        case CustomDarkShade.BlackPearl1:
            return theme.colors.blackPearl1;
        case CustomDarkShade.BlackPearl:
            return theme.colors.blackPearl;
        default:
            return theme.v3.colors.ink;
    }
};

export const isContrastWithDarkHigher = (hex: string) => {
    const { r, g, b } = hexToRgb(hex);
    const luminance = getRelativeLuminance(r, g, b);

    const contrastWithBlack = getContrastRatio(luminance, 0); // black luminance = 0
    const contrastWithWhite = getContrastRatio(luminance, 1); // white luminance = 1

    return contrastWithBlack > contrastWithWhite;
};

function getContrastingBW(hex: string, customDarkShade?: CustomDarkShade): string {
    return isContrastWithDarkHigher(hex) ? (customDarkShade ? getDarkShadeValue(customDarkShade) : theme.v3.colors.ink) : theme.v3.colors.grey96;
}
